
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import TheModal from '@/common/components/UI/Modal/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// @module components
import CashFilters from '@/modules/CashModule/components/CashFilters/index.vue'
import CashPaymentMethod from '@/modules/CashModule/components/CashPaymentMethod/index.vue'

// @types
import type {
    GetCashPaymentStatusParams,
    CashPaymentStatusItem,
    ChangeCashPaymentStatusPayload,
} from '@/new-services/types/cash'

// @new-services
import cashAPI from '@/new-services/cash.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'
import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'

// уточнить
const ACCOUNTANT_ROLE_ID = 58
const OPERATOR_ONEC_ROLE_ID = 63

const TABLE_COLUMNS = [
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'companyName',
        name: 'Клиент'
    },
    {
        key: 'managerName',
        name: 'Менеджер'
    },
    {
        key: 'direction',
        name: 'Направление'
    },
    {
        key: 'cashSum',
        name: 'Сумма перевозки',
    },
    {
        key: 'codPayment',
        name: 'Сумма наложки',
    },
    {
        key: 'courierName',
        name: 'Курьер',
    },
    {
        key: 'paymentMethod',
        name: 'Способ оплаты перевозки',
    },
    {
        key: 'orderCreatedDate',
        name: 'Дата создания',
    },
    {
        key: 'deliveredDate',
        name: 'Дата доставки',
    },
    {
        key: 'deliveryCreatedDate',
        name: 'Дата выдачи',
    },
    {
        key: 'paymentStatusDate',
        name: 'Дата статуса оплаты',
    },
    {
        key: 'invoiceStatus',
        name: 'Статус накладной'
    },
    {
        key: 'paymentStatus',
        name: 'Статус оплаты',
    },
    {
        key: 'paymentStatusWho',
        name: 'Кем проставлен статус'
    }
]

export default defineComponent({
    components: {
        PageTitle,
        CashFilters,
        CashPaymentMethod,
        Loader,
        NewButton,
        NewLimit,
        NewPagination,
        NewTable,
        NewCheckbox,
        TheModal,
        Cheap,
    },
    setup() {
        const notifications = useNotifications()
        const { havePermissions, haveRole } = useAccount()
        const tablesStore = useTablesStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            items: [] as Array<CashPaymentStatusItem>,
            //
            current_page: 1,
            total_pages: 1,
            total_items: 0,
            limit: 50,
            //
            selectedInvoicesIds: [] as Array<number>,
        })

        const export_excel_state = reactive({
            is_loading: false,
        })

        const filters_state = reactive({
            courierId: null as number | null,
            date: null as string | null,
            invoiceNumber: null as string | null,
            statusIds: [] as Array<number>,
            managerIds: [] as Array<number>,
            companyIds: [] as Array<number>,
            invoiceStatusId: null as number | null,
            senderCity: null as number | null,
            receiverCity: null as number | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            senderDispatcherSectorId: null as number | null,
            receiverDispatcherSectorId: null as number | null,
        })

        const accept_modal_state = reactive({
            is_opened: false,
            is_loading: false,
            invoiceIds: [] as Array<number>,
        })

        const loadCashPaymentStatuses = async () => {
            unselectAllItems()

            try {
                state.is_loading = true

                const params: GetCashPaymentStatusParams = {
                    page: state.current_page,
                    limit: state.limit,
                    courierId: filters_state.courierId,
                    date: filters_state.date,
                    invoiceNumber: filters_state.invoiceNumber,
                    statusIds: filters_state.statusIds,
                    managerIds: filters_state.managerIds,
                    companyIds: filters_state.companyIds,
                    invoiceStatusId: filters_state.invoiceStatusId,
                    senderCity: filters_state.senderCity,
                    receiverCity: filters_state.receiverCity,
                    dateFrom: filters_state.dateFrom,
                    dateTo: filters_state.dateTo,
                    senderDispatcherSectorId: filters_state.senderDispatcherSectorId,
                    receiverDispatcherSectorId: filters_state.receiverDispatcherSectorId
                }

                const response = await cashAPI.getCashPaymentStatus(params)
                state.items = response.data.data

                state.total_pages = response.data.meta.last_page
                state.total_items = response.data.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadCashPaymentStatuses)

        const saveFile = (content: File | Blob, file_name: string) => {
            const download_url = window.URL.createObjectURL(content)
            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const onExportExcel = async () => {
            if (export_excel_state.is_loading) return

            try {
                export_excel_state.is_loading = true

                const params: GetCashPaymentStatusParams = {
                    page: state.current_page,
                    limit: state.limit,
                    courierId: filters_state.courierId,
                    date: filters_state.date,
                    invoiceNumber: filters_state.invoiceNumber,
                    statusIds: filters_state.statusIds,
                    managerIds: filters_state.managerIds,
                    companyIds: filters_state.companyIds,
                    invoiceStatusId: filters_state.invoiceStatusId,
                    senderCity: filters_state.senderCity,
                    receiverCity: filters_state.receiverCity,
                    dateFrom: filters_state.dateFrom,
                    dateTo: filters_state.dateTo,
                    senderDispatcherSectorId: filters_state.senderDispatcherSectorId,
                    receiverDispatcherSectorId: filters_state.receiverDispatcherSectorId
                }

                const response = await cashAPI.exportCashPaymentStatuses(params)
                saveFile(response.data, 'Обработка наличных')
            } catch (error) {
                console.error(error)
            } finally {
                export_excel_state.is_loading = false
            }
        }

        const onFiltersSubmit = (params: Omit<GetCashPaymentStatusParams, 'page' | 'limit'>) => {
            filters_state.courierId = params.courierId as number | null
            filters_state.date = params.date as string | null
            filters_state.invoiceNumber = params.invoiceNumber as string | null
            filters_state.statusIds = params.statusIds as Array<number>
            filters_state.managerIds = params.managerIds as Array<number>
            filters_state.companyIds = params.companyIds as Array<number>
            filters_state.invoiceStatusId = params.invoiceStatusId as number | null
            filters_state.senderCity = params.senderCity as number | null
            filters_state.receiverCity = params.receiverCity as number | null
            filters_state.dateFrom = params.dateFrom as string | null
            filters_state.dateTo = params.dateTo as string | null
            filters_state.senderDispatcherSectorId = params.senderDispatcherSectorId as number | null
            filters_state.receiverDispatcherSectorId = params.receiverDispatcherSectorId as number | null

            state.current_page = 1

            loadCashPaymentStatuses()
        }

        const onChangePage = (new_page: number) => {
            state.current_page = new_page
            loadCashPaymentStatuses()
        }

        const onLimitChange = (new_limit: number) => {
            state.limit = new_limit
            loadCashPaymentStatuses()
        }

        const formatPriceToKZT = (price: number | string) => {
            return (
                new Intl.NumberFormat('ru-RU', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(Number(price)) + ' ₸'
            )
        }

        const selectAllItems = () => {
            state.selectedInvoicesIds = state.items
                .filter(isSingleAcceptButtonShow)
                .map((item) => item.invoiceId)
        }

        const unselectAllItems = () => {
            state.selectedInvoicesIds = []
        }

        const onSelectRow = (item: CashPaymentStatusItem) => {
            if (state.selectedInvoicesIds.includes(item.invoiceId)) {
                onUnselectRow(item)
            } else {
                state.selectedInvoicesIds.push(item.invoiceId)
            }
        }

        const onUnselectRow = (item: CashPaymentStatusItem) => {
            const index = state.selectedInvoicesIds.findIndex(
                (item_id) => item_id === item.invoiceId
            )

            if (index >= 0) {
                state.selectedInvoicesIds.splice(index, 1)
            }
        }

        const onAcceptSingleItem = (item: CashPaymentStatusItem) => {
            accept_modal_state.invoiceIds = [item.invoiceId]
            accept_modal_state.is_opened = true
        }

        const onAcceptMultipleItems = () => {
            accept_modal_state.invoiceIds = state.selectedInvoicesIds
            accept_modal_state.is_opened = true
        }

        const onAcceptCancel = () => {
            if (accept_modal_state.is_loading) return
            accept_modal_state.is_opened = false
            accept_modal_state.invoiceIds = []
        }

        const onAcceptCash = async () => {
            try {
                accept_modal_state.is_loading = true

                let statusCode = null

                if (isOperatorOneC.value && !isAccountant.value) {
                    statusCode = 2
                }

                if (isAccountant.value && !isOperatorOneC.value) {
                    statusCode = 3
                }

                if (!statusCode) {
                    notifications.notify({
                        type: 'danger',
                        title: 'Ошибка',
                        description:
                            'Не удалось определить роль пользователя, возможно у вашего аккаунта нет нужной роли или проставлено 2',
                    })

                    return
                }

                const payload: ChangeCashPaymentStatusPayload = {
                    statusCode,
                    invoiceIds: accept_modal_state.invoiceIds,
                }

                await cashAPI.changeCashPaymentStatus(payload)
                notifications.notify({ type: 'success', title: 'Прием наличных успешно завершен' })

                accept_modal_state.is_opened = false
                accept_modal_state.invoiceIds = []

                loadCashPaymentStatuses()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При приеме наличных произошла ошибка' })
            } finally {
                accept_modal_state.is_loading = false
            }
        }

        const isSingleAcceptButtonShow = (item: CashPaymentStatusItem) => {
            if (item.paymentStatus) {
                if (item.paymentStatus.id === 5) {
                    return false
                }
            }

            if (isAccountant.value) {
                if (item.paymentStatus && item.paymentStatus.id === 3) {
                    return false
                }
            }

            if (isOperatorOneC.value || (!isAccountant.value && !isOperatorOneC.value)) {
                if (item.paymentStatus) {
                    if (item.paymentStatus.id === 2 || item.paymentStatus.id === 3) {
                        return false
                    }
                }
            }

            return true
        }

        const getCheapClass = (status_id: number) => {
            if (LtlStatusColors[status_id]) return LtlStatusColors[status_id]
            else if (status_id === 51) return 'red'
            else if (![1, 4].includes(status_id)) return 'yellow'

            return ''
        }

        const selectedItemsCashSum = computed(() => {
            if (state.selectedInvoicesIds.length > 0) {
                let sum = 0

                state.selectedInvoicesIds.forEach((itemId) => {
                    const index = state.items.findIndex((item) => item.invoiceId === itemId)

                    if (index >= 0) {
                        sum +=
                            Number(state.items[index].cashSum) +
                            Number(state.items[index].codPayment)
                    }
                })

                return sum
            }

            return 0
        })

        const acceptingItemsCashSum = computed(() => {
            if (accept_modal_state.invoiceIds.length > 0) {
                let sum = 0

                accept_modal_state.invoiceIds.forEach((itemId) => {
                    const index = state.items.findIndex((item) => item.invoiceId === itemId)

                    if (index >= 0) {
                        sum +=
                            Number(state.items[index].cashSum) +
                            Number(state.items[index].codPayment)
                    }
                })

                return sum
            }

            return 0
        })

        const isAccountant = computed(() => haveRole(ACCOUNTANT_ROLE_ID))
        const isOperatorOneC = computed(() => haveRole(OPERATOR_ONEC_ROLE_ID))
        const canAcceptCash = computed(() => isAccountant.value || isOperatorOneC.value)

        const isMultipleAcceptButtonShowed = computed(() => {
            if (state.selectedInvoicesIds.length > 0) {
                return state.selectedInvoicesIds.every((invoiceId) => {
                    const item = state.items.find((invoice) => invoice.invoiceId === invoiceId)

                    if (item) {
                        return isSingleAcceptButtonShow(item)
                    }

                    return false
                })
            }

            return true
        })

        return {
            tablesStore,
            TABLE_COLUMNS,
            havePermissions,
            state,
            export_excel_state,
            accept_modal_state,
            onFiltersSubmit,
            onChangePage,
            onLimitChange,
            loadCashPaymentStatuses,
            formatPriceToKZT,
            selectAllItems,
            unselectAllItems,
            onSelectRow,
            onAcceptSingleItem,
            onAcceptMultipleItems,
            onAcceptCancel,
            onAcceptCash,
            isSingleAcceptButtonShow,
            selectedItemsCashSum,
            acceptingItemsCashSum,
            isAccountant,
            isOperatorOneC,
            canAcceptCash,
            isMultipleAcceptButtonShowed,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
            getCheapClass,
            onExportExcel,
        }
    },
})
